<template>
    <HeaderCardsLayout v-if="user" variant="No-Logo">
        <template #icon>
            <UserIcon class="w-12 h-12" :class="{ 'text-gray-500': user.activationStatus === 'Deactivated' }" />
        </template>
        <template #headline>
            <PersonInfo
                :name="user.fullName"
                :job-title="user.jobTitle"
                :company="user.company"
                :variant="user.activationStatus"
            />
        </template>
        <template v-if="!isActive" #headline-extra>
            <Badge variant="Red" size="Base">
                <template #icon>
                    <WarningIcon class="size-5" />
                </template>
                {{ $t('user-deactivated') }}
            </Badge>
        </template>
        <template #buttons>
            <template v-if="isActive">
                <LinkButton
                    :disabled-explanation="deactivateDisabledExplanation"
                    :to="{ name: 'user-deactivate', params: { userId: user.id } }"
                    variant="Danger-Strong"
                    size="Base"
                >
                    <template #icon>
                        <BlockIcon class="size-5" />
                    </template>
                    {{ $t('deactivate-user') }}
                </LinkButton>
                <LinkButton
                    :disabled-explanation="editDisabledExplanation"
                    :to="{ name: 'user-edit', params: { userId: user.id } }"
                    variant="Neutral-Strong"
                    size="Base"
                >
                    <template #icon>
                        <EditIcon class="size-5" />
                    </template>
                    {{ $t('edit-user') }}
                </LinkButton>
            </template>
            <template v-else>
                <LinkButton
                    :disabled-explanation="userReactivateDisabledExplanation"
                    :to="{ name: 'user-deactivate', params: { userId: user.id } }"
                    variant="Neutral-Strong"
                    size="Base"
                >
                    <template #icon>
                        <RefreshIcon class="size-5" />
                    </template>
                    {{ $t('reactivate-user') }}
                </LinkButton>
            </template>
        </template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="user.fullName">
                <BreadcrumbLink :target="{ name: 'users' }">
                    <UsersIcon class="mr-1 shrink-0" />
                    {{ $t('users') }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>
        <template #body>
            <div class="space-y-4">
                <PaddedContent class="bg-gray-100">
                    <PersonInfo
                        :name="`${user.lastName}, ${user.firstName}`"
                        :job-title="user.jobTitle"
                        :company="user.company"
                        :variant="user.activationStatus"
                    >
                        <template #avatar>
                            <InitialsWithBackground
                                v-if="!user.avatarUrl"
                                class="size-12 leading-12 shrink-0"
                                :first-name="user.firstName"
                                :last-name="user.lastName"
                                :variant="getInitialsVariant()"
                            />
                            <img
                                v-else
                                :src="user.avatarUrl"
                                alt=""
                                class="rounded-full size-12 object-cover shrink-0"
                            />
                        </template>
                    </PersonInfo>
                </PaddedContent>
                <PaddedContentLeftRight v-if="activeSsoLogins.length > 0 || activePasswordLogin" class="space-y-4">
                    <AccentCard
                        v-for="sso of activeSsoLogins"
                        :key="sso.id"
                        :variant="getSsoAccentCardVariant(sso.provider)"
                    >
                        <template #header="{ headlineId }">
                            <LoginMethodHeader
                                :headline-id="headlineId"
                                :variant="sso.provider === SsoProvider.SsoKm ? 'Koelnmesse-SSO' : 'Generic-SSO'"
                                :is-gray-scale="false"
                            >
                                <template #headline>
                                    {{ $t('provider-login-header', { provider: sso.provider }) }}
                                </template>
                            </LoginMethodHeader>
                        </template>
                        <div class="flex items-start justify-between gap-4 px-2.75 py-3">
                            <ContactInfo
                                v-if="sso.email"
                                :email="sso.email"
                                :phone="sso.phone ?? undefined"
                                :room="sso.room ?? undefined"
                            />
                            <div
                                v-else
                                class="p-2 pl-2.5 flex flex-row items-center gap-2 text-sm text-gray-700 font-light"
                            >
                                <WarningIcon class="size-4 text-gray-500 shrink-0" />
                                <span>{{ $t('data-loading-failed') }}</span>
                            </div>
                        </div>
                    </AccentCard>
                    <AccentCard v-if="user.passwordLogin && user.passwordLogin.isActive" variant="Yellow">
                        <template #header="{ headlineId }">
                            <LoginMethodHeader
                                :headline-id="headlineId"
                                variant="Developer-Account"
                                :is-gray-scale="false"
                            >
                                <template #headline>
                                    {{ $t('email-password-login') }}
                                </template>
                            </LoginMethodHeader>
                        </template>
                        <div class="flex items-start justify-between gap-4 px-2.75 py-3">
                            <CredentialsInfo :email="user.passwordLogin.email ?? ''" :show-password-hint="true" />
                        </div>
                    </AccentCard>
                </PaddedContentLeftRight>
                <PaddedContent class="bg-white">
                    <h2 class="font-semibold text-gray-700 text-l">{{ $t('space-cap-account') }}</h2>
                    <span v-if="isActive" class="text-light text-sm text-gray-500">
                        {{ $t('activated-at', { date: user.activatedAt ?? '-' }) }}
                    </span>
                    <span v-else class="text-light text-sm text-gray-500">
                        {{ $t('deactivated-at', { date: user.deactivatedAt ?? '-' }) }}
                    </span>
                </PaddedContent>
                <PaddedContent class="bg-white pt-3">
                    <h2 class="font-semibold text-gray-700 text-l mb-2">
                        {{ $t('permissions') }}
                    </h2>
                    <RadioButtonGroup
                        :label="$t('access-permission')"
                        name="access-permission"
                        :options="accessPermissionOptions"
                    />
                </PaddedContent>
            </div>
        </template>
    </HeaderCardsLayout>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import AccentCard, { AccentCardVariant } from '@/components/AccentCard/AccentCard.vue';
import Badge from '@/components/Badge/Badge.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import ContactInfo from '@/components/ContactInfo/ContactInfo.vue';
import CredentialsInfo from '@/components/CredentialsInfo/CredentialsInfo.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import BlockIcon from '@/components/Icon/BlockIcon.vue';
import EditIcon from '@/components/Icon/EditIcon.vue';
import RefreshIcon from '@/components/Icon/RefreshIcon.vue';
import UserIcon from '@/components/Icon/UserIcon.vue';
import UsersIcon from '@/components/Icon/UsersIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import PaddedContentLeftRight from '@/components/Layout/PaddedContentLeftRight.vue';
import LoginMethodHeader from '@/components/LoginMethodHeader/LoginMethodHeader.vue';
import PersonInfo from '@/components/PersonInfo/PersonInfo.vue';
import { SsoProvider } from '@/generated/graphql.ts';
import { $t } from '@/plugins/fluent';
import { computed } from 'vue';
import InitialsWithBackground from '../components/InitialsWithBackground.vue';
import { useUser } from '../composables/useUser';

const props = defineProps<{ userId: string }>();

const userId = computed(() => props.userId);
const { user } = useUser(userId);
const isAdmin = computed(() => (user.value ? user.value.isAdmin : false));
const isActive = computed(() => user.value?.isActive ?? false);

const activeSsoLogins = computed(() => user.value?.ssoLogins.filter((sso) => sso.isActive) ?? []);
const activePasswordLogin = computed(() => user.value?.passwordLogin && user.value?.passwordLogin.isActive === true);

const editDisabledExplanation = computed(() => (auth.isAdmin() ? undefined : $t('no-permission-to-edit')));
const deactivateDisabledExplanation = computed(() => (auth.isAdmin() ? undefined : $t('no-permission-to-deactivate')));
const userReactivateDisabledExplanation = computed(() =>
    auth.isAdmin() ? $t('not-implemented') : $t('no-permission-to-reactivate'),
);

function getInitialsVariant() {
    const activationStatus = user.value?.activationStatus;
    if (activationStatus === 'Inactive' || activationStatus === 'Deactivated' || activationStatus === undefined) {
        return 'Gray';
    }
    return 'Default';
}

function getSsoAccentCardVariant(provider: SsoProvider): AccentCardVariant {
    if (provider === SsoProvider.SsoKm) {
        return 'Koelnmesse';
    }
    return 'Yellow';
}

const accessPermissionOptions = computed(() => [
    {
        id: 'read-and-write',
        label: $t('read-and-write'),
        isChecked: isAdmin.value === true && isActive.value,
        isDisabled: true,
        value: 'read-and-write',
    },
    {
        id: 'read',
        label: $t('read'),
        isChecked: isAdmin.value === false && isActive.value,
        isDisabled: true,
        value: 'read',
    },
    {
        id: 'no-read-or-write',
        label: $t('no-read-or-write'),
        isChecked: !isActive.value,
        isDisabled: true,
        value: 'no-read-or-write',
    },
]);
</script>
